import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconDrinking } from '../../assets/images/hardware-first/behavior-icon-drinking.svg';
import { ReactComponent as IconEating } from '../../assets/images/hardware-first/behavior-icon-eating.svg';
import { ReactComponent as IconLicking } from '../../assets/images/hardware-first/behavior-icon-licking.svg';
import { ReactComponent as GraphDrinking } from '../../assets/images/hardware-first/behavior-graph-drinking.svg';
import { ReactComponent as GraphLicking } from '../../assets/images/hardware-first/behavior-graph-licking.svg';
import { ReactComponent as Chip1 } from '../../assets/images/hardware-first/behavior-chip-1.svg';
import { ReactComponent as Chip2 } from '../../assets/images/hardware-first/behavior-chip-2.svg';
import { ReactComponent as Chip3 } from '../../assets/images/hardware-first/behavior-chip-3.svg';
import { ReactComponent as Chip4 } from '../../assets/images/hardware-first/behavior-chip-4.svg';
import { ReactComponent as Chip5 } from '../../assets/images/hardware-first/behavior-chip-5.svg';
import styles from './HardwareFirstBehaviors.module.scss';

/** The eating card's activity bar: solid feed events on a translucent track, unfilled tail. */
function EatingFillGraph() {
  return (
    <div className={styles.fillGraph}>
      <span className={styles.fillSolid} style={{ left: '40%', width: '5.4%' }} />
      <span className={styles.fillSolid} style={{ left: '62.3%', width: '1.2%' }} />
      <span className={styles.fillSolid} style={{ left: '71.9%', width: '1.2%' }} />
      <span className={styles.fillEmpty} />
    </div>
  );
}

// each chip's glyph takes the same hue as its tint
const MORE_BEHAVIOR_CHIPS = [
  { Icon: Chip1, background: 'rgba(154, 88, 254, 0.24)', color: '#9a58fe' },
  { Icon: Chip2, background: 'rgba(20, 205, 113, 0.24)', color: '#14cd71' },
  { Icon: Chip3, background: 'rgba(251, 222, 35, 0.24)', color: '#fbde23' },
  { Icon: Chip4, background: 'rgba(144, 69, 19, 0.3)', color: '#c4692a' },
  { Icon: Chip5, background: 'rgba(255, 255, 255, 0.08)', color: '#ffffff' },
];

/**
 * The pick-your-fi page's AI-behavior band, per the figma redesign: dark surface, stacked behavior
 * cards with live-graph artwork, and the more-behaviors chip row. Scoped to the hardware-first
 * page; the standalone PDPs keep their own PlusNewFeatures carousel.
 */
export default function HardwareFirstBehaviors() {
  const { t } = useTranslation();

  const behaviors = [
    {
      Icon: IconDrinking,
      chipBackground: 'rgba(78, 174, 244, 0.28)',
      iconColor: '#4eaef4',
      title: t('drinking'),
      description: t('hardware_first_behavior_drinking_body'),
      graph: <GraphDrinking className={styles.lineGraph} preserveAspectRatio="none" />,
    },
    {
      Icon: IconEating,
      chipBackground: 'rgba(255, 164, 17, 0.26)',
      iconColor: '#ffa411',
      title: t('eating'),
      description: t('hardware_first_behavior_eating_body'),
      graph: <EatingFillGraph />,
    },
    {
      Icon: IconLicking,
      chipBackground: 'rgba(229, 16, 59, 0.24)',
      iconColor: '#e5103b',
      title: t('licking'),
      description: t('hardware_first_behavior_licking_body'),
      graph: <GraphLicking className={styles.lineGraph} preserveAspectRatio="none" />,
    },
  ];

  return (
    <section className={styles.band}>
      <div className={styles.bandInner}>
        <div className={styles.copy}>
          <h2 className={styles.heading}>{t('hardware_first_behaviors_heading')}</h2>
          <div className={styles.subheading}>{t('hardware_first_behaviors_subheading')}</div>
        </div>
        <div className={styles.cardsColumn}>
          <div className={styles.cards}>
            {behaviors.map((behavior) => (
              <div key={behavior.title} className={styles.card}>
                <div className={styles.cardText}>
                  <div className={styles.cardTitleRow}>
                    <span
                      className={styles.iconChip}
                      style={{ background: behavior.chipBackground, color: behavior.iconColor }}
                    >
                      <behavior.Icon />
                    </span>
                    <span className={styles.cardTitle}>{behavior.title}</span>
                  </div>
                  <div className={styles.cardDescription}>{behavior.description}</div>
                </div>
                <div className={styles.cardGraph}>{behavior.graph}</div>
              </div>
            ))}
          </div>
          <div className={styles.moreBehaviors}>
            <div className={styles.moreTitle}>{t('hardware_first_more_behaviors')}</div>
            <div className={styles.chipRow}>
              {MORE_BEHAVIOR_CHIPS.map(({ Icon, background, color }, index) => (
                <span key={index} className={styles.moreChip} style={{ background, color }}>
                  <Icon />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
